// components/video/ProjectInfo.tsx
import FeatherIcon from 'feather-icons-react';
import React from 'react';
import { Card } from 'react-bootstrap';
import { videoProjectService } from 'services/videoService';

interface ProjectInfoProps {
  project: any;
  currentImage: any;
  images: any[];
  currentTime: number;
  duration: number;
  savedSigns: any[];
  visibleSigns: any[];
  formatTime: (time: number) => string;
  getCurrentFrame: (time: number) => number;
}

const ProjectInfo: React.FC<ProjectInfoProps> = ({
  project,
  currentImage,
  images,
  currentTime,
  duration,
  savedSigns,
  visibleSigns,
  formatTime,
  getCurrentFrame
}) => {
  if (!currentImage) {
    return (
      <Card>
        <Card.Header>
          <h6 className="mb-0">
            <FeatherIcon icon="info" size={16} className="me-2" />
            Informações do Projeto
          </h6>
        </Card.Header>
        <Card.Body>
          <div className="text-center py-4 text-muted">
            <FeatherIcon icon="image" size={48} className="mb-3" />
            <p className="mb-0">Nenhuma imagem correspondente</p>
            <small>
              {images.length === 0
                ? 'Nenhuma imagem carregada'
                : 'Aguarde o vídeo carregar'}
            </small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  // Função auxiliar para formatar números decimais
  const formatDecimal = (value: number | null): string => {
    if (value === null || value === undefined) return 'N/A';
    return value.toFixed(2);
  };

  // Verificar se há dados de orientação disponíveis
  const hasOrientationData =
    currentImage.yaw !== null ||
    currentImage.pitch !== null ||
    currentImage.roll !== null ||
    currentImage.bearing !== null;

  return (
    <Card>
      <Card.Header>
        <h6 className="mb-0">
          <FeatherIcon icon="info" size={16} className="me-2" />
          Informações do Projeto
        </h6>
      </Card.Header>
      <Card.Body>
        {/* Nome do Projeto */}
        <div className="mb-2">
          <small className="text-muted d-block">Nome:</small>
          <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
            {project.name || 'N/A'}
          </span>
        </div>

        {/* Descrição */}
        <div className="mb-2">
          <small className="text-muted d-block">Descrição:</small>
          <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
            {project.description || 'N/A'}
          </span>
        </div>

        {/* Criado por */}
        <div className="mb-2">
          <small className="text-muted d-block">Criado por:</small>
          <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
            {project.user?.name || 'N/A'}
          </span>
        </div>

        {/* Total de capturas */}
        <div className="mb-2">
          <small className="text-muted d-block">Total de capturas:</small>
          <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
            {images.length}
          </span>
        </div>

        <hr className="my-2" />

        {/* Imagem Atual */}
        <div className="mb-2">
          <small className="text-muted d-block">
            <strong>Imagem Atual</strong>
          </small>
        </div>

        {/* Timestamp */}
        <div className="mb-2">
          <small className="text-muted d-block">Timestamp:</small>
          <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
            {videoProjectService.getFormattedTimestamp(currentImage.timestamp)}
          </span>
        </div>

        {/* Tamanho do arquivo */}
        <div className="mb-2">
          <small className="text-muted d-block">Tamanho:</small>
          <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
            {videoProjectService.getFormattedFileSize(currentImage.size)}
          </span>
        </div>

        {/* GPS da imagem */}
        {currentImage.lat !== null && currentImage.long !== null && (
          <div className="mb-2">
            <small className="text-muted d-block">GPS:</small>
            <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
              {currentImage.lat.toFixed(6)}, {currentImage.long.toFixed(6)}
            </span>
          </div>
        )}

        {/* Orientação da Câmera - NOVO BLOCO */}
        {hasOrientationData && (
          <>
            <div className="mb-2 mt-2">
              <small className="text-muted d-block">
                <FeatherIcon icon="compass" size={12} className="me-1" />
                <strong>Orientação da Câmera</strong>
              </small>
            </div>

            <div className="ps-2">
              {/* Yaw (Guinada) */}
              {currentImage.yaw !== null && (
                <div className="mb-1">
                  <small className="text-muted d-block">Yaw (Guinada):</small>
                  <span
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  >
                    {formatDecimal(currentImage.yaw)}°
                  </span>
                </div>
              )}

              {/* Pitch (Arfagem) */}
              {currentImage.pitch !== null && (
                <div className="mb-1">
                  <small className="text-muted d-block">Pitch (Arfagem):</small>
                  <span
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  >
                    {formatDecimal(currentImage.pitch)}°
                  </span>
                </div>
              )}

              {/* Roll (Rolagem) */}
              {currentImage.roll !== null && (
                <div className="mb-1">
                  <small className="text-muted d-block">Roll (Rolagem):</small>
                  <span
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  >
                    {formatDecimal(currentImage.roll)}°
                  </span>
                </div>
              )}

              {/* Bearing (Direção) */}
              {currentImage.bearing !== null && (
                <div className="mb-1">
                  <small className="text-muted d-block">
                    Bearing (Direção):
                  </small>
                  <span
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  >
                    {formatDecimal(currentImage.bearing)}°
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        <hr className="my-2" />

        {/* Informações do Vídeo */}
        <div className="mb-2">
          <small className="text-muted d-block">
            <strong>Vídeo</strong>
          </small>
        </div>

        {/* Tempo Atual */}
        <div className="mb-2">
          <small className="text-muted d-block">Tempo:</small>
          <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        {/* Frame */}
        <div className="mb-2">
          <small className="text-muted d-block">Frame:</small>
          <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
            {getCurrentFrame(currentTime)} /{' '}
            {duration > 0 ? getCurrentFrame(duration) : 0}
          </span>
        </div>

        {/* Estatísticas de Sinalizações */}
        {savedSigns.length > 0 && (
          <>
            <hr className="my-2" />
            <div className="p-2 bg-light rounded">
              <small className="text-muted d-block mb-1">
                <strong>Sinalizações</strong>
              </small>
              <div className="d-flex justify-content-between">
                <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  <span className="text-success">Total:</span>{' '}
                  {savedSigns.length}
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  <span className="text-primary">Neste frame:</span>{' '}
                  {visibleSigns.length}
                </span>
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ProjectInfo;
